import { useState, useMemo, useCallback } from 'react';
import { FiSearch, FiAlertCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Auth } from 'aws-amplify';
import appConfig from '../../../config/app-config';
import useFetch from '../../../hooks/useFetch';
import TextEdit from '../../molecules/TextEdit';
import SelectTextField from '../../molecules/SelectTextField';
import ControlEditWithIcon from '../../molecules/ControlEditWithIcon';
import {
    formatCurrency,
    calculateIntensity,
    getIntensityColor,
    type OperationType,
} from './collateralUtils';
import { collateralApi } from '../../../services/collateralApi';

const API = appConfig.apis.convertXmlApiEnv;

interface OperationOption {
    id: string;
    operationCode: string;
    assignorName: string;
    assignorCnpj: string;
    status: string;
    totalDocuments: number;
    totalReceivables: number;
    totalAmount: number;
    operationType: OperationType;
    obligation: string;
    traderName: string;
    collateralAnalysisId?: string;
    sentToValidation?: boolean;
}

interface Props {
    onClose: () => void;
    onCreated: () => void;
}

interface PersonResult {
    uuid: string;
    name: string;
    alias: string | null;
}

const VALIDATION_PROCESSES = [
    { key: 'validation_xml', label: 'XML' },
    { key: 'validation_invoice_pdf', label: 'NF PDF' },
    { key: 'validation_receipt_stub', label: 'Canhoto' },
    { key: 'validation_accepted_bill', label: 'Duplicata c/ Aceite' },
    { key: 'validation_contact', label: 'Validação Sacado' },
    { key: 'validation_purchase_contract', label: 'Contrato C/V' },
    { key: 'validation_sefaz', label: 'SEFAZ' },
];

const CRITERIA_OPTIONS = [
    { value: 'DO_NOT_VALIDATE', label: 'Não validar' },
    { value: 'PRE_DISBURSEMENT', label: 'Pré-desembolso' },
    { value: 'POST_DISBURSEMENT', label: 'Pós-desembolso' },
];

const OPERATION_TYPE_OPTIONS = [
    { value: 'CESSAO', label: 'Cessão' },
    { value: 'CPRF', label: 'CPRF' },
];

const OBLIGATION_OPTIONS = [
    { value: 'WITHOUT_RECOURSE', label: 'Sem Regresso' },
    { value: 'WITH_RECOURSE', label: 'Com Regresso' },
    { value: 'NO_OBLIGATION', label: 'Sem Garantia' },
];

const LoadingOverlay = ({ message }: { message: string }) => (
    <div className="absolute inset-0 bg-white/70 dark:bg-slate-800/70 z-10 flex items-center justify-center rounded-lg">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <svg className="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {message}
        </div>
    </div>
);

const NewAnalysisFromOperation = ({ onClose, onCreated }: Props) => {
    const [step, setStep] = useState<'select' | 'configure'>('select');
    const [selected, setSelected] = useState<OperationOption | null>(null);
    const [search, setSearch] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    const [operationType, setOperationType] = useState('CESSAO');
    const [obligationType, setObligationType] = useState('WITHOUT_RECOURSE');
    const [traderName, setTraderName] = useState('');
    const [traderResults, setTraderResults] = useState<PersonResult[]>([]);
    const [showTraderDropdown, setShowTraderDropdown] = useState(false);
    const [isSearchingTrader, setIsSearchingTrader] = useState(false);

    const [validationConfig, setValidationConfig] = useState<Record<string, string>>({
        validation_xml: 'PRE_DISBURSEMENT',
        validation_invoice_pdf: 'DO_NOT_VALIDATE',
        validation_receipt_stub: 'DO_NOT_VALIDATE',
        validation_accepted_bill: 'DO_NOT_VALIDATE',
        validation_contact: 'DO_NOT_VALIDATE',
        validation_purchase_contract: 'DO_NOT_VALIDATE',
        validation_sefaz: 'DO_NOT_VALIDATE',
    });
    const [analysisCriteria, setAnalysisCriteria] = useState<'Completa' | 'Amostragem'>('Amostragem');
    const [sampledTitles, setSampledTitles] = useState(1);
    const [debtorNotificationRequested, setDebtorNotificationRequested] = useState(false);

    const fetchHeaders = useMemo(() => [
        { key: 'x-api-key', value: API.key || '' },
        { key: 'Accept-Charset', value: 'utf-8' },
    ], []);
    const { isLoading, data } = useFetch(`${API.address}/operations?page=0&pageSize=50`, fetchHeaders);

    const operations: OperationOption[] = useMemo(() => {
        if (!data?.list) return [];
        // Filter out operations that already have a collateral analysis
        return data.list.filter((o: OperationOption) => !o.collateralAnalysisId);
    }, [data]);

    const filteredOps = useMemo(() => {
        if (!search) return operations;
        const s = search.toLowerCase();
        return operations.filter(o => o.operationCode?.toLowerCase().includes(s) || o.assignorName?.toLowerCase().includes(s));
    }, [operations, search]);

    const intensity = useMemo(() => {
        if (!selected) return null;
        const e = (k: string) => validationConfig[k] !== 'DO_NOT_VALIDATE';
        return calculateIntensity(
            { xml: e('validation_xml'), nf: e('validation_invoice_pdf'), canhoto: e('validation_receipt_stub'), duplicata: e('validation_accepted_bill') },
            selected.totalReceivables || 0
        );
    }, [selected, validationConfig]);

    const searchTrader = useCallback(async (query: string) => {
        if (query.length < 3) { setTraderResults([]); setShowTraderDropdown(false); return; }
        setIsSearchingTrader(true);
        try {
            const results = await collateralApi.searchLegalPerson(query);
            setTraderResults(results);
            setShowTraderDropdown(true);
        } catch { /* ignore */ }
        finally { setIsSearchingTrader(false); }
    }, []);

    const handleSelectTrader = (person: PersonResult) => {
        setTraderName(person.name);
        setShowTraderDropdown(false);
    };

    const handleSelectOperation = (op: OperationOption) => {
        setSelected(op);
        setOperationType(op.operationType || 'CESSAO');
        setObligationType(op.obligation || 'WITHOUT_RECOURSE');
        setTraderName(op.traderName || '');
    };

    const handleCreate = async () => {
        if (!selected) return;
        setIsCreating(true);
        try {
            const result = await collateralApi.createAnalysis({
                offer_operation_id: selected.id,
                offer_operation_code: selected.operationCode,
                offer_operation_type: operationType,
                obligation_type: obligationType,
                trader_name: traderName,
                assignor_name: selected.assignorName || '',
                assignor_tax_id: selected.assignorCnpj || '',
                validation_config: {
                    ...validationConfig,
                    analysis_criteria: analysisCriteria,
                    analysis_receivables_count: analysisCriteria === 'Amostragem' ? sampledTitles : null,
                },
                total_receivables: selected.totalReceivables || 0,
                total_amount: selected.totalAmount || 0,
                debtor_notification_requested: debtorNotificationRequested,
            });

            const analysisId = result?.collateral_analysis_id;

            const fetchHeaders: Record<string, string> = { 'x-api-key': API.key || '' };
            const session = await Auth.currentSession();
            fetchHeaders['Authorization'] = `Bearer ${session.getIdToken().getJwtToken()}`;

            const opRes = await fetch(`${API.address}/operations/${selected.id}`, { headers: fetchHeaders });
            const opData = await opRes.json();
            const documents = opData?.documents || [];

            if (analysisId && documents.length > 0) {
                await collateralApi.populateReceivables(analysisId, documents);
            }

            onCreated();
        } catch (e: any) {
            if (e?.status === 409) toast.warn('Análise já existe para esta operação.');
            else { console.error('Error creating analysis:', e); toast.error('Erro ao criar análise. Tente novamente.'); }
        } finally { setIsCreating(false); }
    };

    // Step 1: Select operation
    if (step === 'select') {
        return (
            <div className="relative">
                {isLoading && <LoadingOverlay message="Carregando operações..." />}
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Nova Análise de Garantia</h3>
                    <button onClick={() => selected && setStep('configure')} disabled={!selected}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 text-sm">Próximo</button>
                </div>

                <div className="relative mb-3">
                    <FiSearch className="absolute left-3 top-2.5 text-gray-400" size={16} />
                    <input type="text" placeholder="Buscar por código ou cedente..." value={search} onChange={e => setSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 dark:text-white" />
                </div>

                <div className="space-y-2">
                    {filteredOps.length === 0 && !isLoading ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">Nenhuma operação disponível</div>
                    ) : filteredOps.map(op => (
                        <div key={op.id} onClick={() => handleSelectOperation(op)}
                            className={`p-3 rounded-lg border transition-colors cursor-pointer ${selected?.id === op.id
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-400'
                                : 'border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600'}`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-sm dark:text-white">{op.operationCode}</span>
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${op.status === 'closed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                            {op.status === 'closed' ? 'Fechado' : 'Aberto'}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{op.assignorName}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-medium dark:text-white">{formatCurrency(op.totalAmount || 0)}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{op.totalReceivables || 0} títulos · {op.totalDocuments || 0} docs</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Step 2: Configure analysis
    const totalTitles = selected!.totalReceivables || 1;
    const samplePercentage = totalTitles > 0 ? ((sampledTitles / totalTitles) * 100) : 0;

    return (
        <div className="relative">
            {isCreating && <LoadingOverlay message="Aguarde, criando análise..." />}
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Configurar Análise</h3>
                <div className="flex items-center gap-3">
                    <button onClick={() => setStep('select')} disabled={isCreating}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-300 text-sm disabled:opacity-50">Voltar</button>
                    <button onClick={handleCreate} disabled={isCreating}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm disabled:opacity-50">
                        {isCreating ? 'Criando...' : 'Criar Análise'}
                    </button>
                </div>
            </div>

            <div className="space-y-5">
                {/* Operation summary */}
                <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 text-sm">
                    <p className="text-gray-700 dark:text-gray-300 font-medium">{selected!.operationCode} — {selected!.assignorName}</p>
                    <div className="text-gray-500 dark:text-gray-400">{formatCurrency(selected!.totalAmount || 0)} · {selected!.totalReceivables || 0} títulos</div>
                </div>

                {/* Operation type, obligation, trader */}
                <SelectTextField
                    label="Tipo Operação"
                    value={operationType}
                    onChange={e => setOperationType(e.target.value)}
                    items={OPERATION_TYPE_OPTIONS}
                    emptyLabel="Selecione..."
                    disabled={isCreating}
                />
                <SelectTextField
                    label="Obrigação"
                    value={obligationType}
                    onChange={e => setObligationType(e.target.value)}
                    items={OBLIGATION_OPTIONS}
                    emptyLabel="Selecione..."
                    disabled={isCreating}
                />

                {/* Trader search */}
                <div className="relative">
                    <TextEdit label="Trader (Enter para buscar)" value={traderName}
                        onChange={e => setTraderName(e.target.value)}
                        disabled={isCreating}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchTrader(traderName); } }} />
                    {isSearchingTrader && <div className="text-xs text-gray-400 mt-1">Buscando...</div>}
                    {showTraderDropdown && traderResults.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-auto">
                            {traderResults.slice(0, 6).map(p => (
                                <div key={p.uuid} onClick={() => handleSelectTrader(p)}
                                    className="px-3 py-2 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer text-sm">
                                    <span className="font-medium dark:text-white">{p.name}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Validation processes - single column */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Processos de Validação</label>
                    <div className="space-y-3">
                        {VALIDATION_PROCESSES.map(({ key, label }) => (
                            <SelectTextField
                                key={key}
                                label={label}
                                value={validationConfig[key]}
                                onChange={e => setValidationConfig(prev => ({ ...prev, [key]: e.target.value }))}
                                items={CRITERIA_OPTIONS}
                                emptyValue=""
                                emptyLabel="Selecione..."
                                disabled={isCreating}
                            />
                        ))}
                    </div>
                </div>

                {/* Intensity preview */}
                {intensity && (
                    <div className="flex items-center gap-3 bg-gray-50 dark:bg-slate-900 rounded-lg p-3">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Intensidade:</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getIntensityColor(intensity.level)}`}>
                            {intensity.level} (score: {intensity.score})
                        </span>
                    </div>
                )}

                {/* Debtor notification */}
                <label className="flex items-center gap-2 cursor-pointer py-2">
                    <input type="checkbox" checked={debtorNotificationRequested} onChange={e => setDebtorNotificationRequested(e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" disabled={isCreating} />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Cedente deve notificar sacados</span>
                </label>

                {/* Criteria + Sampling below */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Critério de Análise</label>
                    <div className="flex items-center gap-4 mb-3">
                        {(['Completa', 'Amostragem'] as const).map(opt => (
                            <label key={opt} className="flex items-center gap-2 cursor-pointer">
                                <input type="radio" value={opt} checked={analysisCriteria === opt}
                                    onChange={() => setAnalysisCriteria(opt)} disabled={isCreating}
                                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" />
                                <span className="text-sm text-gray-900 dark:text-gray-100">{opt}</span>
                            </label>
                        ))}
                    </div>

                    {analysisCriteria === 'Amostragem' && (
                        <div className="flex items-center gap-3">
                            <div style={{ minWidth: '11rem' }}>
                                <ControlEditWithIcon
                                    label=""
                                    text="títulos"
                                    minValue={1}
                                    maxValue={totalTitles}
                                    value={sampledTitles}
                                    onIncrement={setSampledTitles}
                                    onDecrement={setSampledTitles}
                                    disabled={isCreating}
                                />
                            </div>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                {samplePercentage.toFixed(1)}%
                            </span>
                        </div>
                    )}
                </div>

                {/* Open batch warning */}
                {selected && selected.status !== 'closed' && (
                    <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                        <FiAlertCircle className="text-amber-600 mt-0.5 flex-shrink-0" size={16} />
                        <div className="text-sm text-amber-800 dark:text-amber-300">
                            <strong>Lote aberto:</strong> NFes podem entrar ou sair desta operação. Você poderá re-sincronizar quando o lote for fechado.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NewAnalysisFromOperation;
